(function () {
  "use strict";

  const loadTimestamp = Date.now();

  // =========================================================
  // GLOBAL INITIATOR CONTEXT
  // =========================================================

  let lastInteraction = null;

  const INTERACTION_TIMEOUT = 5000;


  // =========================================================
  // DOM ELEMENT INFORMATION
  // =========================================================

  function getElementDetails(element) {
    if (!element || element.nodeType !== 1) {
      return null;
    }

    const rect = element.getBoundingClientRect();

    return {
      tagName: element.tagName,
      id: element.id || null,
      name: element.getAttribute("name"),
      type: element.getAttribute("type"),
      value: element.getAttribute("value"),
      text: (element.innerText || element.textContent || "")
        .trim()
        .substring(0, 300),

      classes: element.className
        ? String(element.className)
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 20)
        : [],

      ariaLabel: element.getAttribute("aria-label"),

      dataAttributes: Array.from(element.attributes || [])
        .filter(attr => attr.name.startsWith("data-"))
        .reduce((obj, attr) => {
          obj[attr.name] = attr.value;
          return obj;
        }, {}),

      formAction: element.form?.action || null,
      formMethod: element.form?.method || null,

      selector: getElementSelector(element),

      rect: {
        x: Math.round(rect.x),
        y: Math.round(rect.y),
        width: Math.round(rect.width),
        height: Math.round(rect.height)
      }
    };
  }


  // =========================================================
  // GENERATE CSS SELECTOR
  // =========================================================

  function getElementSelector(element) {
    if (!element || element.nodeType !== 1) {
      return null;
    }

    if (element.id) {
      return `#${CSS.escape(element.id)}`;
    }

    const parts = [];

    let current = element;

    while (
      current &&
      current.nodeType === 1 &&
      current !== document.body
    ) {
      let selector = current.tagName.toLowerCase();

      if (current.id) {
        selector += `#${CSS.escape(current.id)}`;
        parts.unshift(selector);
        break;
      }

      const classes = Array.from(current.classList || [])
        .slice(0, 3)
        .map(c => `.${CSS.escape(c)}`)
        .join("");

      selector += classes;

      const parent = current.parentElement;

      if (parent) {
        const siblings = Array.from(parent.children)
          .filter(child => child.tagName === current.tagName);

        if (siblings.length > 1) {
          const index = siblings.indexOf(current) + 1;
          selector += `:nth-of-type(${index})`;
        }
      }

      parts.unshift(selector);

      current = current.parentElement;

      if (parts.length >= 6) {
        break;
      }
    }

    return parts.join(" > ");
  }


  // =========================================================
  // CAPTURE USER INTERACTIONS
  // =========================================================

  function captureInteraction(event) {
    let target = event.target;

    if (!target) {
      return;
    }

    // Find the most relevant clickable/form element.
    const relevantElement =
      target.closest?.(
        'button, input, select, textarea, form, a, [role="button"], [type="submit"]'
      ) || target;

    lastInteraction = {
      timestamp: Date.now(),

      eventType: event.type,

      element: getElementDetails(relevantElement),

      originalTarget: getElementDetails(target),

      path: event.composedPath
        ? event.composedPath()
            .filter(node => node && node.nodeType === 1)
            .slice(0, 10)
            .map(node => getElementDetails(node))
        : [],

      x: event.clientX ?? null,
      y: event.clientY ?? null,

      isTrusted: event.isTrusted,

      stack: new Error().stack
    };
  }


  // Capture early in the event lifecycle.
  [
    "click",
    "pointerdown",
    "mousedown",
    "touchstart",
    "submit"
  ].forEach(eventName => {
    document.addEventListener(
      eventName,
      captureInteraction,
      true
    );
  });


  // =========================================================
  // FORM SUBMIT TRACKING
  // =========================================================

  document.addEventListener(
    "submit",
    function (event) {
      const form = event.target;

      if (!form) {
        return;
      }

      lastInteraction = {
        timestamp: Date.now(),

        eventType: "submit",

        element: getElementDetails(form),

        form: {
          action: form.action,
          method: form.method,
          id: form.id || null,
          name: form.name || null,

          className: form.className
            ? String(form.className)
            : null
        },

        stack: new Error().stack,

        isTrusted: event.isTrusted
      };
    },
    true
  );


  // =========================================================
  // CURRENT INITIATOR
  // =========================================================

  function getInitiator() {
    const now = Date.now();

    const interaction =
      lastInteraction &&
      now - lastInteraction.timestamp <= INTERACTION_TIMEOUT
        ? lastInteraction
        : null;

    return {
      captured: !!interaction,

      ageMs: interaction
        ? now - interaction.timestamp
        : null,

      interaction
    };
  }


  // =========================================================
  // REQUEST STACK
  // =========================================================

  function getRequestStack() {
    return new Error().stack
      ?.split("\n")
      .slice(2, 15)
      .join("\n") || null;
  }


  // =========================================================
  // GET ALL DETAILS
  // =========================================================

  function getProperties(requestDetails = {}) {
    const initiator = getInitiator();

    return {
      __product_timestamp:
        `${loadTimestamp}_${Date.now()}`,

      __product_add_details: JSON.stringify({
        version: "2.0",

        // Page
        url: location.href,
        referrer: document.referrer,

        // Browser
        userAgent: navigator.userAgent,
        language: navigator.language,
        languages: navigator.languages,
        platform: navigator.platform,
        timezone:
          Intl.DateTimeFormat().resolvedOptions().timeZone,

        screen:
          `${screen.width}x${screen.height}`,

        viewport:
          `${window.innerWidth}x${window.innerHeight}`,

        cookiesEnabled:
          navigator.cookieEnabled,

        online:
          navigator.onLine,

        webdriver:
          navigator.webdriver,

        // Request
        request: requestDetails,

        // Actual DOM interaction
        initiator,

        // Stack from inside fetch/XHR interception
        requestStack:
          getRequestStack(),

        // Performance information
        navigationType:
          performance.getEntriesByType("navigation")[0]
            ?.type || null,

        timestamp: new Date().toISOString()
      })
    };
  }


  // =========================================================
  // BODY MODIFICATION
  // =========================================================

  function modifyBody(body, requestDetails = {}) {
    const props = getProperties(requestDetails);


    // -------------------------------------------------------
    // FormData
    // -------------------------------------------------------

    if (body instanceof FormData) {
      const indexes = new Set();

      for (const key of body.keys()) {
        const match = key.match(/^items\[(\d+)\]/);

        if (match) {
          indexes.add(match[1]);
        }
      }

      if (indexes.size) {
        indexes.forEach(index => {
          Object.entries(props).forEach(([key, value]) => {
            body.set(
              `items[${index}][properties][${key}]`,
              value
            );
          });
        });
      } else {
        body.set(
          "properties[__product_timestamp]",
          props.__product_timestamp
        );

        body.set(
          "properties[__product_add_details]",
          props.__product_add_details
        );
      }

      return body;
    }


    // -------------------------------------------------------
    // String body
    // -------------------------------------------------------

    if (typeof body === "string") {

      // JSON
      try {
        const data = JSON.parse(body);

        if (Array.isArray(data.items)) {
          data.items.forEach(item => {
            item.properties = {
              ...(item.properties || {}),
              ...props
            };
          });

          return JSON.stringify(data);
        }

        if (data.id) {
          data.properties = {
            ...(data.properties || {}),
            ...props
          };

          return JSON.stringify(data);
        }
      } catch {}


      // URL encoded
      try {
        const params = new URLSearchParams(body);

        const indexes = new Set();

        for (const key of params.keys()) {
          const match = key.match(/^items\[(\d+)\]/);

          if (match) {
            indexes.add(match[1]);
          }
        }

        if (indexes.size) {
          indexes.forEach(index => {
            Object.entries(props).forEach(([key, value]) => {
              params.set(
                `items[${index}][properties][${key}]`,
                value
              );
            });
          });
        } else {
          params.set(
            "properties[__product_timestamp]",
            props.__product_timestamp
          );

          params.set(
            "properties[__product_add_details]",
            props.__product_add_details
          );
        }

        return params.toString();
      } catch {}
    }

    return body;
  }


  // =========================================================
  // XMLHttpRequest
  // =========================================================

  const originalOpen =
    XMLHttpRequest.prototype.open;

  const originalSend =
    XMLHttpRequest.prototype.send;


  XMLHttpRequest.prototype.open = function (
    method,
    url,
    ...args
  ) {
    this._cartMethod = method;
    this._cartUrl = url;

    return originalOpen.call(
      this,
      method,
      url,
      ...args
    );
  };


  XMLHttpRequest.prototype.send = function (body) {

    if (
      this._cartMethod?.toUpperCase() === "POST" &&
      String(this._cartUrl).includes("/cart/add")
    ) {

      body = modifyBody(body, {
        transport: "XMLHttpRequest",

        method:
          this._cartMethod?.toUpperCase(),

        url:
          this._cartUrl,

        bodyType:
          body?.constructor?.name || typeof body,

        xhrReadyState:
          this.readyState
      });
    }

    return originalSend.call(
      this,
      body
    );
  };


  // =========================================================
  // FETCH
  // =========================================================

  const originalFetch = window.fetch;


  window.fetch = function (input, options = {}) {

    const url =
      typeof input === "string"
        ? input
        : input?.url || "";


    const method = (
      options?.method ||
      (
        input instanceof Request
          ? input.method
          : "GET"
      )
    ).toUpperCase();


    if (
      method === "POST" &&
      url.includes("/cart/add")
    ) {

      const requestDetails = {
        transport: "fetch",

        method,

        url,

        bodyType:
          options?.body?.constructor?.name ||
          typeof options?.body,

        credentials:
          options?.credentials ||
          (
            input instanceof Request
              ? input.credentials
              : null
          )
      };


      // ---------------------------------------------
      // fetch(url, { body })
      // ---------------------------------------------

      if (
        options &&
        Object.prototype.hasOwnProperty.call(
          options,
          "body"
        )
      ) {

        options.body =
          modifyBody(
            options.body,
            requestDetails
          );

        return originalFetch.call(
          this,
          input,
          options
        );
      }


      // ---------------------------------------------
      // fetch(new Request(...))
      // ---------------------------------------------

      if (
        input instanceof Request &&
        input.body
      ) {

        return input
          .clone()
          .text()
          .then(body => {

            const modifiedBody =
              modifyBody(
                body,
                requestDetails
              );


            const request =
              new Request(
                input,
                {
                  body: modifiedBody
                }
              );


            return originalFetch.call(
              this,
              request
            );
          });
      }
    }


    return originalFetch.call(
      this,
      input,
      options
    );
  };


  // =========================================================
  // DEBUG
  // =========================================================

  window.__shopifyCartTracker = {
    getLastInteraction() {
      return lastInteraction;
    },

    getInitiator() {
      return getInitiator();
    }
  };

})();